import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import CrearLiga from './CrearLiga';
import './styles/index.css';

const BarDashboard = ({ onGoToTorneos, onBack }) => {
  const [user] = useState(api.getCurrentUser());
  const esRecaudador = user?.tipoUsuario === 'recaudador';
  const [ligas, setLigas] = useState([]);
  const [selectedLiga, setSelectedLiga] = useState(null);
  const [clasificacion, setClasificacion] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedJornadas, setExpandedJornadas] = useState(new Set());
  const [expandedPartidas, setExpandedPartidas] = useState(new Set());
  const [editingPartida, setEditingPartida] = useState(null);
  const [editForm, setEditForm] = useState({
    ganadorId: '',
    puntosJugador1: '',
    puntosJugador2: '',
    ciento80sJugador1: '',
    ciento80sJugador2: '',
    pagado: false
  });
  
  // Estado para mostrar pantalla de crear liga
  const [showCrearLiga, setShowCrearLiga] = useState(false);
  
  // Estados para modales de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  
  // Estados para buscadores
  const [searchJugador1, setSearchJugador1] = useState('');
  const [searchJugador2, setSearchJugador2] = useState('');
  const [suggestionSelected1, setSuggestionSelected1] = useState(false);
  const [suggestionSelected2, setSuggestionSelected2] = useState(false);

  useEffect(() => {
    loadLigas();
  }, []);

  useEffect(() => {
    if (selectedLiga) {
      loadLigaData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLiga]);

  const loadLigas = async () => {
    try {
      setLoading(true);
      const ligasData = await api.getMyLeagues();
      setLigas(ligasData);
      
      if (ligasData.length > 0) {
        setSelectedLiga(ligasData[0]._id);
      }
    } catch (err) {
      setError('Error cargando ligas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLigaData = async () => {
    try {
      setLoading(true);
      const [clasif, jorn] = await Promise.all([
        api.getClasificacion(selectedLiga),
        api.getJornadas(selectedLiga)
      ]);
      
      setClasificacion(clasif);
      setJornadas(jorn);
      setError('');
    } catch (err) {
      setError('Error cargando datos de la liga');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleJornada = (jornadaNumero) => {
    const newExpanded = new Set();
    
    // Si la jornada ya está abierta, la cerramos
    if (expandedJornadas.has(jornadaNumero)) {
      newExpanded.delete(jornadaNumero);
    } else {
      // Si no está abierta, cerramos todas y abrimos solo esta
      newExpanded.add(jornadaNumero);
      
      // Hacer scroll a la jornada después de un pequeño delay para que se renderice
      setTimeout(() => {
        const jornadaElement = document.querySelector(`[data-jornada-numero="${jornadaNumero}"]`);
        if (jornadaElement) {
          jornadaElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
    
    setExpandedJornadas(newExpanded);
  };

  const formatMatchStatValue = (value, options = {}) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return options.fallback || '-';
    const decimals = options.decimals ?? 0;
    const suffix = options.suffix || '';
    return `${numericValue.toFixed(decimals)}${suffix}`;
  };

  const togglePartida = (partidaId) => {
    setExpandedPartidas(prev => (prev.has(partidaId) ? new Set() : new Set([partidaId])));
  };

  // Verificar si hay partidos jugados sin pagar en una jornada
  const tienePartidosSinPagar = (jornada) => {
    return jornada.partidas.some(partida => {
      // Ignorar partidos de descanso
      if (partida.esDescanso) {
        return false;
      }
      
      // Solo contar partidos que tienen resultado
      const tieneResultado = partida.resultado?.ganador || partida.ganador;
      if (!tieneResultado) {
        return false;
      }
      
      // Verificar si NO está pagada - solo verificar el campo 'pagada'
      const estaPagada = partida.pagada === true;
      return !estaPagada;
    });
  };

  const startEditingPartida = (jornada, partida) => {
    setEditingPartida({ jornadaNumero: jornada.jornada, partidaId: partida._id });
    
    // Obtener 180s de donde estén disponibles (ciento80s o count180s)
    const ciento80s1 = partida.ciento80sJugador1 || partida.count180s?.jugador1 || 0;
    const ciento80s2 = partida.ciento80sJugador2 || partida.count180s?.jugador2 || 0;
    
    // Obtener resultado si existe
    const ganadorId = partida.resultado?.ganador?._id || partida.ganador?._id || '';
    const sets1 = partida.resultado?.partidasGanadas?.jugador1 || 0;
    const sets2 = partida.resultado?.partidasGanadas?.jugador2 || 0;
    
    setEditForm({
      ganadorId: ganadorId,
      puntosJugador1: sets1,
      puntosJugador2: sets2,
      ciento80sJugador1: ciento80s1,
      ciento80sJugador2: ciento80s2,
      pagado: partida.pagada || false
    });
  };

  const cancelEditing = () => {
    setEditingPartida(null);
    setEditForm({ 
      ganadorId: '', 
      puntosJugador1: '', 
      puntosJugador2: '',
      ciento80sJugador1: '',
      ciento80sJugador2: '',
      pagado: false
    });
  };

  // Función helper para mostrar modal de confirmación
  const showConfirm = (title, message, onConfirm) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => onConfirm);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const savePartidaResult = async (jornada, partida) => {
    try {
      const sets1 = parseInt(editForm.puntosJugador1) || 0;
      const sets2 = parseInt(editForm.puntosJugador2) || 0;
      
      // Validar que haya un ganador claro
      if (sets1 === sets2) {
        alert('Debe haber un ganador. Los sets no pueden ser iguales.');
        return;
      }
      
      // Validar que el ganador seleccionado coincida con los sets
      const ganadorEsperado = sets1 > sets2 ? partida.jugador1._id : partida.jugador2._id;
      if (editForm.ganadorId !== ganadorEsperado) {
        alert('El ganador seleccionado no coincide con los sets ingresados.');
        return;
      }
      
      const resultado = {
        ganadorId: editForm.ganadorId,
        setsJugador1: sets1,
        setsJugador2: sets2,
        ciento80sJugador1: parseInt(editForm.ciento80sJugador1) || 0,
        ciento80sJugador2: parseInt(editForm.ciento80sJugador2) || 0,
        pagado: editForm.pagado
      };

      await api.updatePartidaResult(selectedLiga, jornada.jornada, partida._id, resultado);
      
      // Recargar datos
      await loadLigaData();
      cancelEditing();
    } catch (err) {
      alert('Error actualizando resultado: ' + err.message);
      console.error(err);
    }
  };

  const handleLogout = () => {
    api.logout();
    window.location.reload();
  };

  // Obtener sugerencias para el buscador 1
  const sugerenciasJugador1 = useMemo(() => {
    if (!searchJugador1.trim() || searchJugador1.trim().length < 2 || suggestionSelected1) {
      return [];
    }

    const jugadoresSet = new Set();
    const searchLower = searchJugador1.toLowerCase().trim();

    jornadas.forEach(jornada => {
      jornada.partidas.forEach(partida => {
        const nombre1 = partida.jugador1.username || partida.jugador1.nombre;
        const nombre2 = partida.jugador2.username || partida.jugador2.nombre;
        
        if (nombre1 && nombre1.toLowerCase().includes(searchLower)) {
          jugadoresSet.add(nombre1);
        }
        if (nombre2 && nombre2.toLowerCase().includes(searchLower)) {
          jugadoresSet.add(nombre2);
        }
      });
    });

    return Array.from(jugadoresSet).slice(0, 5); // Máximo 5 sugerencias
  }, [jornadas, searchJugador1, suggestionSelected1]);

  // Obtener sugerencias para el buscador 2
  const sugerenciasJugador2 = useMemo(() => {
    if (!searchJugador2.trim() || searchJugador2.trim().length < 2 || suggestionSelected2) {
      return [];
    }

    const jugadoresSet = new Set();
    const searchLower = searchJugador2.toLowerCase().trim();

    jornadas.forEach(jornada => {
      jornada.partidas.forEach(partida => {
        const nombre1 = partida.jugador1.username || partida.jugador1.nombre;
        const nombre2 = partida.jugador2.username || partida.jugador2.nombre;
        
        if (nombre1 && nombre1.toLowerCase().includes(searchLower)) {
          jugadoresSet.add(nombre1);
        }
        if (nombre2 && nombre2.toLowerCase().includes(searchLower)) {
          jugadoresSet.add(nombre2);
        }
      });
    });

    return Array.from(jugadoresSet).slice(0, 5); // Máximo 5 sugerencias
  }, [jornadas, searchJugador2, suggestionSelected2]);

  // Filtrar jornadas según los buscadores
  const jornadasFiltradas = useMemo(() => jornadas.filter(jornada => {
    // Si no hay búsqueda, mostrar todas las jornadas
    if (!searchJugador1 && !searchJugador2) {
      return true;
    }

    // Verificar si alguna partida de la jornada coincide con los filtros
    return jornada.partidas.some(partida => {
      const jugador1Match = !searchJugador1 || 
        (partida.jugador1.username || partida.jugador1.nombre || '').toLowerCase().includes(searchJugador1.toLowerCase()) ||
        (partida.jugador2.username || partida.jugador2.nombre || '').toLowerCase().includes(searchJugador1.toLowerCase());
      
      const jugador2Match = !searchJugador2 || 
        (partida.jugador1.username || partida.jugador1.nombre || '').toLowerCase().includes(searchJugador2.toLowerCase()) ||
        (partida.jugador2.username || partida.jugador2.nombre || '').toLowerCase().includes(searchJugador2.toLowerCase());
      
      return jugador1Match && jugador2Match;
    });
  }), [jornadas, searchJugador1, searchJugador2]);

  if (loading && ligas.length === 0) {
    return <div className="dashboard-container"><div className="loading">Cargando...</div></div>;
  }

  return (
    <div className="dashboard-container">
      {/* Pantalla de Crear Liga */}
      {showCrearLiga ? (
        <CrearLiga 
          onBack={() => setShowCrearLiga(false)}
          onLigaCreada={(liga) => {
            setLigas(prev => [liga, ...prev]);
            setSelectedLiga(liga._id);
            setShowCrearLiga(false);
          }}
        />
      ) : (
        <>
      {/* Header Profesional */}
      <div className="dashboard-header dashboard-header-pro">
        {/* Izquierda: Logo + Nombre */}
        <div className="header-brand">
          <img src="/Logo26.png" alt="501 Dart League" className="dashboard-logo" />
          <div className="header-brand-text">
            <h1>501 Dart League</h1>
            <span className="header-brand-subtitle">Gestión de Ligas</span>
          </div>
        </div>
        
        {/* Derecha: Usuario + Acciones */}
        <div className="header-user-section">
          <div className="header-user-info">
            <span className="header-greeting">Hola, <strong>{user.username || user.nombre}</strong></span>
            <span className="admin-badge">⚙️ Administrador</span>
          </div>
          <div className="header-actions">
            {onBack && (
              <button onClick={onBack} className="btn-action btn-outline-action">
                ← Inicio
              </button>
            )}
            {onGoToTorneos && (
              <button onClick={onGoToTorneos} className="btn-action btn-primary-action">
                🏆 Torneos
              </button>
            )}
            <button onClick={handleLogout} className="btn-action btn-secondary-action">
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Selector de Liga */}
      {ligas.length > 0 && (
        <div className="liga-selector">
          <label htmlFor="liga-select">Seleccionar Liga:</label>
          <select
            id="liga-select"
            value={selectedLiga || ''}
            onChange={(e) => setSelectedLiga(e.target.value)}
            className="liga-select"
          >
            {ligas.map(liga => (
              <option key={liga._id} value={liga._id}>
                {esRecaudador && liga.bar?.nombre ? `${liga.bar.nombre} · ${liga.nombre}` : liga.nombre}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedLiga && (
        <>
          {/* Clasificación */}
          <div className="section">
            <h2 className="section-title">Clasificación</h2>
            {loading ? (
              <div className="loading">Cargando clasificación...</div>
            ) : clasificacion.length > 0 ? (
              <div className="table-container">
                <table className="clasificacion-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{selectedLiga?.modalidad === 'parejas' ? 'Equipo' : 'Jugador'}</th>
                      <th>PTS</th>
                      <th>PJ</th>
                      <th>G</th>
                      <th>P</th>
                      <th>JG-JP</th>
                      <th>DIF</th>
                      <th>180s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clasificacion.map((item, index) => {
                      const diferencia = (item.juegosGanados || 0) - (item.juegosPerdidos || 0);
                      const total180s = item.ciento80s || item.total180s || 0;
                      
                      // Obtener nombre: equipo para parejas, jugador para individual
                      const getNombre = () => {
                        if (item.equipo) {
                          return item.equipo.nombreEquipo || `Equipo ${item.equipo.equipoIndex + 1}`;
                        }
                        return item.jugador?.username || item.jugador?.nombre || 'N/A';
                      };
                      
                      return (
                        <tr key={item.jugador?._id || item.equipo?.equipoIndex || index} className={index < 3 ? `top-${index + 1}` : ''}>
                          <td className={`pos pos-${index + 1}`}>
                            {index < 3 ? (
                              <span className="medal-emoji">
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </span>
                            ) : (
                              index + 1
                            )}
                          </td>
                          <td className="jugador">{getNombre()}</td>
                          <td className="points"><strong>{item.puntos || 0}</strong></td>
                          <td>{item.partidas || 0}</td>
                          <td className="wins">{item.victorias || 0}</td>
                          <td className="losses">{item.derrotas || 0}</td>
                          <td className="games-diff">{item.juegosGanados || 0}-{item.juegosPerdidos || 0}</td>
                          <td className={`diff ${diferencia > 0 ? 'positive' : diferencia < 0 ? 'negative' : 'neutral'}`}>
                            {diferencia > 0 ? '+' : ''}{diferencia}
                          </td>
                          <td className="ciento80s">{total180s}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-state">No hay datos de clasificación disponibles</p>
            )}
          </div>

          {/* Jornadas */}
          <div className="section">
            <h2 className="section-title">Jornadas</h2>
            
            {/* Buscadores */}
            <div className="search-filters">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Buscar Jugador 1..."
                  value={searchJugador1}
                  onChange={(e) => {
                    setSearchJugador1(e.target.value);
                    setSuggestionSelected1(false);
                  }}
                  className="search-input"
                />
                {searchJugador1 && (
                  <button 
                    onClick={() => {
                      setSearchJugador1('');
                      setSuggestionSelected1(false);
                    }}
                    className="clear-search"
                    title="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
                
                {/* Sugerencias para Jugador 1 */}
                {searchJugador1.trim().length >= 2 && sugerenciasJugador1.length > 0 && (
                <div className="suggestions-list">
                    {sugerenciasJugador1.map((nombreJugador) => (
                      <div 
                        key={nombreJugador}
                        className="suggestion-item"
                        onClick={() => {
                          setSearchJugador1(nombreJugador);
                          setSuggestionSelected1(true);
                        }}
                      >
                        {nombreJugador}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Buscar Jugador 2..."
                  value={searchJugador2}
                  onChange={(e) => {
                    setSearchJugador2(e.target.value);
                    setSuggestionSelected2(false);
                  }}
                  className="search-input"
                />
                {searchJugador2 && (
                  <button 
                    onClick={() => {
                      setSearchJugador2('');
                      setSuggestionSelected2(false);
                    }}
                    className="clear-search"
                    title="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
                
                {/* Sugerencias para Jugador 2 */}
                {searchJugador2.trim().length >= 2 && sugerenciasJugador2.length > 0 && (
                <div className="suggestions-list">
                    {sugerenciasJugador2.map((nombreJugador) => (
                      <div 
                        key={nombreJugador}
                        className="suggestion-item"
                        onClick={() => {
                          setSearchJugador2(nombreJugador);
                          setSuggestionSelected2(true);
                        }}
                      >
                        {nombreJugador}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {(searchJugador1 || searchJugador2) && (
                <button 
                  onClick={() => {
                    setSearchJugador1('');
                    setSearchJugador2('');
                    setSuggestionSelected1(false);
                    setSuggestionSelected2(false);
                  }}
                  className="clear-all-btn"
                >
                  Limpiar todo
                </button>
              )}
            </div>

            {loading ? (
              <div className="loading">Cargando jornadas...</div>
            ) : jornadasFiltradas.length > 0 ? (
              <div 
                className="jornadas-list"
                style={{ 
                  marginTop: (
                    (searchJugador1.trim().length >= 2 && sugerenciasJugador1.length > 0) ||
                    (searchJugador2.trim().length >= 2 && sugerenciasJugador2.length > 0)
                  ) ? '150px' : '0' 
                }}
              >
                {jornadasFiltradas.map((jornada) => (
                  <div key={`jornada-${jornada.jornada}`} className="jornada-wrapper" data-jornada-numero={jornada.jornada}>
                    <div
                      className="jornada-header"
                      onClick={() => toggleJornada(jornada.jornada)}
                    >
                      {tienePartidosSinPagar(jornada) && (
                        <span className="pago-warning" title="Hay partidos jugados sin pagar">
                          ⚠️ Partido sin pagar
                        </span>
                      )}
                      <div className="jornada-title-row">
                        <h3>Jornada {jornada.jornada}</h3>
                      </div>
                      <span className="expand-icon">
                        {expandedJornadas.has(jornada.jornada) ? '▼' : '▶'}
                      </span>
                    </div>

                    {expandedJornadas.has(jornada.jornada) && (
                      <div className="jornada-content">
                        {jornada.partidas.length > 0 ? (
                          <div className="partidas-list">
                            {jornada.partidas.map(partida => {
                              const isEditing = editingPartida?.jornadaNumero === jornada.jornada && 
                                               editingPartida?.partidaId === partida._id;
                              const isPartidaExpanded = expandedPartidas.has(partida._id);
                              const canExpandStats = partida.completada && partida.promedioAveragesPartida;
                              const advancedStats = partida.estadisticasAvanzadas;
                              const advancedStatsDisponibles = Boolean(advancedStats);
                              const ganadorId = partida.resultado?.ganador?._id || partida.resultado?.ganador;
                              const player1Id = partida.jugador1._id;
                              const player2Id = partida.jugador2._id;
                              const player1Name = partida.nombreEquipo1 || partida.jugador1.username || partida.jugador1.nombre;
                              const player2Name = partida.nombreEquipo2 || partida.jugador2.username || partida.jugador2.nombre;
                              const scoreJugador1 = partida.completada && partida.resultado
                                ? (partida.resultado.partidasGanadas?.jugador1 ?? 0)
                                : null;
                              const scoreJugador2 = partida.completada && partida.resultado
                                ? (partida.resultado.partidasGanadas?.jugador2 ?? 0)
                                : null;
                              const esGanador1 = ganadorId === player1Id;
                              const esGanador2 = ganadorId === player2Id;

                              return (
                                <div key={partida._id} className={`partida-item${canExpandStats ? ' expandable' : ''}`}>
                                  {isEditing ? (
                                    <div className="edit-form">
                                      <div className="edit-row">
                                        <label>Ganador:</label>
                                        <select
                                          value={editForm.ganadorId}
                                          onChange={(e) => setEditForm({...editForm, ganadorId: e.target.value})}
                                        >
                                          <option value="">Sin definir</option>
                                          <option value={partida.jugador1._id}>
                                            {partida.jugador1.username || partida.jugador1.nombre}
                                          </option>
                                          <option value={partida.jugador2._id}>
                                            {partida.jugador2.username || partida.jugador2.nombre}
                                          </option>
                                        </select>
                                      </div>
                                      <div className="edit-row">
                                        <label>Sets ganados {partida.jugador1.username}:</label>
                                        <input
                                          type="number"
                                          value={editForm.puntosJugador1}
                                          onChange={(e) => setEditForm({...editForm, puntosJugador1: e.target.value})}
                                          min="0"
                                          max="10"
                                        />
                                      </div>
                                      <div className="edit-row">
                                        <label>Sets ganados {partida.jugador2.username}:</label>
                                        <input
                                          type="number"
                                          value={editForm.puntosJugador2}
                                          onChange={(e) => setEditForm({...editForm, puntosJugador2: e.target.value})}
                                          min="0"
                                          max="10"
                                        />
                                      </div>
                                      <div className="edit-row">
                                        <label>180s {partida.jugador1.username}:</label>
                                        <input
                                          type="number"
                                          value={editForm.ciento80sJugador1}
                                          onChange={(e) => setEditForm({...editForm, ciento80sJugador1: e.target.value})}
                                          min="0"
                                        />
                                      </div>
                                      <div className="edit-row">
                                        <label>180s {partida.jugador2.username}:</label>
                                        <input
                                          type="number"
                                          value={editForm.ciento80sJugador2}
                                          onChange={(e) => setEditForm({...editForm, ciento80sJugador2: e.target.value})}
                                          min="0"
                                        />
                                      </div>
                                      <div className="edit-row">
                                        <label>
                                          <span style={{marginRight: '8px'}}>Partida pagada</span>
                                          <input
                                            type="checkbox"
                                            checked={editForm.pagado}
                                            onChange={(e) => setEditForm({...editForm, pagado: e.target.checked})}
                                          />
                                        </label>
                                      </div>
                                      <div className="edit-actions">
                                        {partida.completada && (
                                          <button 
                                            onClick={() => {
                                              showConfirm(
                                                '⚠️ Resetear Partida',
                                                '¿Estás seguro de resetear esta partida? Se perderán todos los datos: resultado, 180s, average y puntuación.',
                                                async () => {
                                                  try {
                                                    await api.resetPartida(partida._id);
                                                    await loadLigaData();
                                                    cancelEditing();
                                                  } catch (err) {
                                                    alert('Error reseteando partida: ' + err.message);
                                                  }
                                                }
                                              );
                                            }} 
                                            className="reset-btn"
                                          >
                                            🔄 Resetear
                                          </button>
                                        )}
                                        <button onClick={() => savePartidaResult(jornada, partida)} className="save-btn">
                                          Guardar
                                        </button>
                                        <button onClick={cancelEditing} className="cancel-btn">
                                          Cancelar
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="partida-header-row">
                                        {/* Mostrar apuntador si existe */}
                                        {partida.apuntador && (
                                          <div className="apuntador-badge">
                                            <span className="apuntador-badge-label">Apunta</span>
                                            <span className="apuntador-badge-name">{partida.nombreEquipoApuntador || partida.apuntador.username || partida.apuntador.nombre}</span>
                                          </div>
                                        )}
                                        <div className="partida-players">
                                          <div className={`partida-player${esGanador1 ? ' winner' : ''}`}>
                                            <div className="partida-player-name">{player1Name}</div>
                                            {scoreJugador1 !== null && (
                                              <div className="partida-player-score">{scoreJugador1}</div>
                                            )}
                                          </div>
                                          <span className="vs">vs</span>
                                          <div className={`partida-player${esGanador2 ? ' winner' : ''}`}>
                                            <div className="partida-player-name">{player2Name}</div>
                                            {scoreJugador2 !== null && (
                                              <div className="partida-player-score">{scoreJugador2}</div>
                                            )}
                                          </div>
                                        </div>

                                        <div className="partida-actions">
                                          {/* Checkbox de pagado */}
                                          <label 
                                            className={`pago-checkbox ${partida.pagada ? 'pagada' : 'pendiente'}`}
                                            title={partida.pagada ? "Partida pagada" : "Marcar como pagada"}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={partida.pagada || false}
                                              onChange={(e) => {
                                                const isChecking = e.target.checked;
                                                if (isChecking) {
                                                  // Solo confirmar cuando se marca como pagada
                                                  showConfirm(
                                                    '💰 Confirmar Pago',
                                                    '¿Estás seguro de marcar esta partida como pagada?',
                                                    async () => {
                                                      try {
                                                        await api.togglePagoPartida(partida._id, true);
                                                        await loadLigaData();
                                                      } catch (err) {
                                                        alert('Error actualizando estado de pago: ' + err.message);
                                                      }
                                                    }
                                                  );
                                                } else {
                                                  // Desmarcar sin confirmar
                                                  (async () => {
                                                    try {
                                                      await api.togglePagoPartida(partida._id, false);
                                                      await loadLigaData();
                                                    } catch (err) {
                                                      alert('Error actualizando estado de pago: ' + err.message);
                                                    }
                                                  })();
                                                }
                                              }}
                                            />
                                            <span className="pago-text">
                                              {partida.pagada ? '✓ Pagada' : 'Pagar'}
                                            </span>
                                          </label>

                                          {canExpandStats && (
                                            <button
                                              type="button"
                                              className={`ver-stats-btn${isPartidaExpanded ? ' expanded' : ''}`}
                                              aria-expanded={isPartidaExpanded}
                                              aria-label={isPartidaExpanded ? 'Ocultar estadísticas' : 'Ver estadísticas'}
                                              onClick={(e) => { e.stopPropagation(); togglePartida(partida._id); }}
                                            >
                                              <span className="ver-stats-text ver-stats-text-desktop">
                                                {isPartidaExpanded ? 'Ocultar' : 'Stats'}
                                              </span>
                                              <span className="ver-stats-icon" aria-hidden="true">
                                                {isPartidaExpanded ? '▼' : '▶'}
                                              </span>
                                            </button>
                                          )}

                                          <button
                                            onClick={() => startEditingPartida(jornada, partida)}
                                            className="edit-btn-icon"
                                            title="Editar resultado"
                                          >
                                            ✏️
                                          </button>
                                        </div>
                                      </div>

                                      {/* Panel de estadísticas expandible para admin */}
                                      {canExpandStats && isPartidaExpanded && (
                                        <div className="partida-estadisticas admin-stats-panel">
                                          <div className="admin-stats-names">
                                            <span className={`admin-stats-name admin-stats-name-left${esGanador1 ? ' winner' : ''}`}>
                                              {player1Name}
                                            </span>
                                            <span className="admin-stats-separator">vs</span>
                                            <span className={`admin-stats-name admin-stats-name-right${esGanador2 ? ' winner' : ''}`}>
                                              {player2Name}
                                            </span>
                                          </div>
                                          {[
                                            { label: 'Resultado', p1: partida.resultado?.partidasGanadas ? String(partida.resultado.partidasGanadas.jugador1 ?? 0) : '-', p2: partida.resultado?.partidasGanadas ? String(partida.resultado.partidasGanadas.jugador2 ?? 0) : '-', primary: true, winner1: esGanador1, winner2: esGanador2 },
                                            { label: 'AVRG', p1: formatMatchStatValue(partida.promedioAveragesPartida.jugador1, { decimals: 1, fallback: '0.0' }), p2: formatMatchStatValue(partida.promedioAveragesPartida.jugador2, { decimals: 1, fallback: '0.0' }) },
                                            { label: '180', p1: String(partida.count180s?.jugador1 || 0), p2: String(partida.count180s?.jugador2 || 0), highlight: true },
                                            { label: '100+', p1: advancedStatsDisponibles ? String(advancedStats.jugador1?.count100Plus || 0) : '-', p2: advancedStatsDisponibles ? String(advancedStats.jugador2?.count100Plus || 0) : '-' },
                                            { label: '140+', p1: advancedStatsDisponibles ? String(advancedStats.jugador1?.count140Plus || 0) : '-', p2: advancedStatsDisponibles ? String(advancedStats.jugador2?.count140Plus || 0) : '-' },
                                            { label: 'Checkout %', p1: advancedStatsDisponibles ? formatMatchStatValue(advancedStats.jugador1?.checkoutPct, { decimals: 1, suffix: '%' }) : '-', p2: advancedStatsDisponibles ? formatMatchStatValue(advancedStats.jugador2?.checkoutPct, { decimals: 1, suffix: '%' }) : '-' },
                                            { label: 'High Finish', p1: advancedStatsDisponibles && (advancedStats.jugador1?.highFinish || 0) > 0 ? String(advancedStats.jugador1.highFinish) : '-', p2: advancedStatsDisponibles && (advancedStats.jugador2?.highFinish || 0) > 0 ? String(advancedStats.jugador2.highFinish) : '-' },
                                          ].map(row => (
                                            <div key={row.label} className={`partida-stat-row${row.primary ? ' primary' : ''}`}>
                                              <span className={`partida-stat-value left${row.primary ? ' primary' : ''}${row.highlight ? ' highlight' : ''}${row.winner1 ? ' winner' : ''}`}>{row.p1}</span>
                                              <span className={`partida-stat-label${row.highlight ? ' highlight' : ''}${row.primary ? ' primary' : ''}`}>{row.label}</span>
                                              <span className={`partida-stat-value right${row.primary ? ' primary' : ''}${row.highlight ? ' highlight' : ''}${row.winner2 ? ' winner' : ''}`}>{row.p2}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="empty-jornada">No hay partidas en esta jornada</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-state">
                {searchJugador1 || searchJugador2 
                  ? 'No se encontraron jornadas con esos jugadores' 
                  : 'No hay jornadas disponibles'}
              </p>
            )}
          </div>
        </>
      )}

      {ligas.length === 0 && !loading && (
        <div className="empty-state-box">
          <h2>No tienes ligas creadas</h2>
          <p>Crea tu primera liga para comenzar</p>
          {!esRecaudador && (
            <button onClick={() => setShowCrearLiga(true)} className="btn-primary" style={{ marginTop: '20px' }}>
              ➕ Crear Liga
            </button>
          )}
        </div>
      )}

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{confirmTitle}</h3>
            </div>
            <div className="modal-body">
              <p>{confirmMessage}</p>
            </div>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={handleCancel}>
                Cancelar
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={handleConfirm}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default BarDashboard;